import { isToday, isThisWeek, isThisMonth, parseISO } from 'date-fns';
import { TournamentType } from '@bg-components/Profile/enums';
import { IHowToPlaySection } from '@bg-shared/business/HowToPlay';
import { GamificationCapType } from '@bg-shared/business/Gamification/enums';
import { IIntroPageData } from '../../../../../components/src/components/Profile/interfaces';
import {
    IGamificationAsset,
    IGamificationAssetPurchase,
    IGamificationModel,
    INotificationMessage,
} from '../interfaces';
import { getTournamentPriority } from '../utils';
import {
    GAMIFICATION_TERMS_AND_CONDITIONS_BLOCK_ORDER,
    INTRO_PAGE_ORDERED_PRESET,
} from '../constants';

type ICapChecker = {
    [K in GamificationCapType]: {
        isInPeriod(date: Date): boolean;
        getCapLimit(item: IGamificationAsset): number;
    };
};

const capCheckers: ICapChecker = {
    [GamificationCapType.DAILY]: {
        isInPeriod: isToday,
        getCapLimit: (item) => item.dailyCapPerPlayer,
    },
    [GamificationCapType.WEEKLY]: {
        isInPeriod: isThisWeek,
        getCapLimit: (item) => item.weeklyCapPerPlayer,
    },
    [GamificationCapType.MONTHLY]: {
        isInPeriod: isThisMonth,
        getCapLimit: (item) => item.monthlyCapPerPlayer,
    },
};

export const gamificationSelectors = {
    hasError: (state: IGamificationModel): boolean => state.settings.error,

    settingsLoaded: (state: IGamificationModel): boolean => state.settings.settingsLoaded,

    isLoaded: (state: IGamificationModel): boolean => state.settings.loaded,

    isBlocked: (state: IGamificationModel): boolean => state.profile.blocked,

    hasAccount: (state: IGamificationModel): boolean => !!state.profile.userInfo.id,

    getUserInfo: (state: IGamificationModel): IGamificationModel['profile']['userInfo'] =>
        state.profile.userInfo,

    getInboxes: (state: IGamificationModel): IGamificationModel['inboxes'] => state.inboxes,

    getPlayerName: (state: IGamificationModel) => state.profile.userInfo.name,

    getAchievedBadges: (state: IGamificationModel): IGamificationModel['badges']['achieved'] =>
        state.badges.achieved,

    getChallengesBadges: (state: IGamificationModel): IGamificationModel['badges']['challenges'] =>
        state.badges.challenges,

    getUnSeenInboxes: (state: IGamificationModel): IGamificationModel['inboxes'] =>
        state.inboxes.filter((inbox) => inbox.unseen),

    getUnSeenAchievements: (state: IGamificationModel): IGamificationModel['badges']['achieved'] =>
        state.badges.achieved.filter((inbox) => inbox.unseen),

    getUnreadInboxesCount: (state: IGamificationModel): number =>
        state.inboxes.filter((inbox) => inbox.unread).length,

    getUnreadAchievedBadgesCount: (state: IGamificationModel): number =>
        state.badges.achieved.filter((achievement) => achievement.unread).length,

    getUnreadChallengesBadgesCount: (state: IGamificationModel): number =>
        state.badges.challenges.filter((badge) => badge.unread).length,

    getLevel: (state: IGamificationModel): IGamificationModel['profile']['level'] =>
        state.profile.level,

    getNotificationMessage: (state: IGamificationModel): INotificationMessage =>
        state.notifications.notificationMessage,

    getTournamentRewards: (tournamentId: string) => (state: IGamificationModel) =>
        state.tournaments.list.find((item) => item.id === tournamentId)?.rewards ?? [],

    getTournaments: (state: IGamificationModel) => {
        const currentDate = new Date();

        return [...state.tournaments.list].sort((a, b) => {
            const aActiveFrom = new Date(a.activeFromDate);
            const aDisabledFrom = new Date(a.disabledFromDate);
            const bActiveFrom = new Date(b.activeFromDate);
            const bDisabledFrom = new Date(b.disabledFromDate);

            const aPriority = getTournamentPriority(currentDate, aActiveFrom, aDisabledFrom);
            const bPriority = getTournamentPriority(currentDate, bActiveFrom, bDisabledFrom);

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            if (aPriority === TournamentType.ONGOING) {
                return aActiveFrom.getTime() - bActiveFrom.getTime();
            }

            if (aPriority === TournamentType.UPCOMING) {
                return aActiveFrom.getTime() - bActiveFrom.getTime();
            }

            return bDisabledFrom.getTime() - aDisabledFrom.getTime();
        });
    },

    isOptInTournament: (tournamentId: string) => (state: IGamificationModel) =>
        state.tournaments.optInIds.includes(tournamentId),

    getAvailableStockShopItems: (state: IGamificationModel): IGamificationAsset[] =>
        state.shop.items
            .filter((asset) => asset.unlimitedStock || asset.stock > 0)
            .filter((item) => {
                const purchases = state.shop.purchasesByHashes[item.id] || [];
                const checker = capCheckers[item.capType];

                const periodPurchases = purchases.filter((p) =>
                    checker.isInPeriod(parseISO(p.createdAt)),
                );

                return periodPurchases.length < checker.getCapLimit(item);
            }),

    getAcquiredItems: (
        state: IGamificationModel,
    ): Array<{ asset: IGamificationAsset; purchase: IGamificationAssetPurchase }> =>
        Object.entries(state.shop.purchasesByHashes)
            .reduce<Array<{ asset: IGamificationAsset; purchase: IGamificationAssetPurchase }>>(
                (acc, [id, purchases]) => {
                    const asset = state.shop.items.find((assetItem) => assetItem.id === id);

                    if (asset) {
                        purchases.forEach((purchase) => {
                            acc.push({
                                asset,
                                purchase,
                            });
                        });
                    }

                    return acc;
                },
                [],
            )
            .sort(
                (a, b) =>
                    new Date(b.purchase.createdAt).getTime() -
                    new Date(a.purchase.createdAt).getTime(),
            ),

    getIntroPageTranslations: (state: IGamificationModel): IHowToPlaySection[] =>
        state.introPageTranslations?.blocks || [],

    getIntroTabBlocks: (state: IGamificationModel): IIntroPageData[] => {
        const data = gamificationSelectors.getIntroPageTranslations(state);
        return data.slice(0, GAMIFICATION_TERMS_AND_CONDITIONS_BLOCK_ORDER).map((item, index) => ({
            ...item,
            ...INTRO_PAGE_ORDERED_PRESET[index],
        }));
    },

    getTermsAndConditionsBlock: (state: IGamificationModel): IHowToPlaySection => {
        const data = gamificationSelectors.getIntroPageTranslations(state);
        return data[GAMIFICATION_TERMS_AND_CONDITIONS_BLOCK_ORDER];
    },

    getIntroductionVideo: (state: IGamificationModel): IHowToPlaySection => {
        const data = gamificationSelectors.getIntroPageTranslations(state);

        return data[data.length - 1];
    },
};
